/**
 * Game Calculations and Utilities
 *
 * Centralized module for pet conditions, level progression, and XP calculations.
 * All logic matches the Move smart contract implementation.
 */

// ============================================================================
// Pet Conditions
// ============================================================================

/**
 * Pet overall condition states.
 * Simplified enum combining maintenance needs with emotional states.
 *
 * Priority order: SICK > HUNGRY > DIRTY > TIRED > SAD > UNRULY > HAPPY
 *
 * Note: "Happy" state also triggers Active Care XP bonus (passive XP per tick).
 * Happy requires all stats >= 25, making it both a mood indicator and care quality metric.
 * "Unruly" state blocks Active Care XP - pet is not earning passive XP per tick.
 * "Sad" state indicates low happiness (< 50) - cosmetic indicator.
 */
export enum PetCondition {
  SICK = 1,    // Health < 25 (critical, needs medicine)
  HUNGRY = 2,  // Hunger < 25 (needs food)
  DIRTY = 3,   // Cleanliness < 25 (needs cleaning)
  TIRED = 4,   // Energy < 25 (needs rest)
  SAD = 7,     // Happiness < 50 (needs cheering up)
  UNRULY = 6,  // Discipline < 25 (needs training, blocks Active Care XP)
  HAPPY = 5,   // All stats >= 25 (earns Active Care XP)
}

export type PetConditionValue = PetCondition;

/**
 * Human-readable labels for pet conditions.
 */
export const PetConditionLabel: Record<PetConditionValue, string> = {
  [PetCondition.SICK]: "Sick",
  [PetCondition.HUNGRY]: "Hungry",
  [PetCondition.DIRTY]: "Dirty",
  [PetCondition.TIRED]: "Tired",
  [PetCondition.SAD]: "Sad",
  [PetCondition.UNRULY]: "Unruly",
  [PetCondition.HAPPY]: "Happy",
};

/**
 * Helper to get condition label from numeric value.
 */
export function getConditionLabel(condition: number): string {
  return PetConditionLabel[condition as PetConditionValue] || "Unknown";
}

/**
 * Helper to check if pet is in egg stage (stage === 0).
 */
export function isEgg(stage: number): boolean {
  return stage === 0;
}

/**
 * Helper to check if pet is sick (overall_condition === SICK).
 */
export function isSick(overall_condition: number): boolean {
  return overall_condition === PetCondition.SICK;
}

/**
 * Helper to check if pet is hungry (overall_condition === HUNGRY).
 */
export function isHungry(overall_condition: number): boolean {
  return overall_condition === PetCondition.HUNGRY;
}

/**
 * Helper to check if pet is dirty (overall_condition === DIRTY).
 */
export function isDirty(overall_condition: number): boolean {
  return overall_condition === PetCondition.DIRTY;
}

/**
 * Helper to check if pet is tired (overall_condition === TIRED).
 */
export function isTired(overall_condition: number): boolean {
  return overall_condition === PetCondition.TIRED;
}

/**
 * Helper to check if pet is unruly (overall_condition === UNRULY).
 * Unruly pets do NOT earn Active Care XP per tick.
 */
export function isUnruly(overall_condition: number): boolean {
  return overall_condition === PetCondition.UNRULY;
}

/**
 * Helper to check if pet is happy (overall_condition === HAPPY).
 * Happy pets earn Active Care XP bonus per tick.
 */
export function isHappy(overall_condition: number): boolean {
  return overall_condition === PetCondition.HAPPY;
}

/**
 * Determines if a pet needs immediate care based on condition.
 * Critical conditions that require player attention: Sick, Hungry, Dirty, Unruly.
 * Tired and Happy states are considered normal gameplay and don't trigger attention.
 *
 * @param overall_condition - The pet's current condition (1-6)
 * @returns true if pet needs immediate care
 */
export function needsCare(overall_condition: number): boolean {
  return (
    overall_condition === PetCondition.SICK ||
    overall_condition === PetCondition.HUNGRY ||
    overall_condition === PetCondition.DIRTY ||
    overall_condition === PetCondition.UNRULY
  );
}

// ============================================================================
// Level and XP Calculations
// ============================================================================

/**
 * Level and XP calculation utilities.
 * XP progression is fully off-chain (not in Move contract).
 *
 * Level calculation (linear progression):
 * - XP threshold for level N = (N - 1) * 1000
 * - Examples:
 *   - Level 1 needs 0 XP total (starting level)
 *   - Level 2 needs 1000 XP total
 *   - Level 3 needs 2000 XP total
 *   - Level 4 needs 3000 XP total
 * - Each level requires exactly 1000 XP to advance
 */

export const XP_PER_LEVEL = 1000;

/**
 * Calculate XP threshold for reaching a specific level using linear progression.
 * Formula: (level - 1) * 1000
 * @param level - Target level
 * @returns Total XP needed to reach that level
 * @example
 * calculateLevelThreshold(1) // 0 XP (starting level)
 * calculateLevelThreshold(2) // 1000 XP
 * calculateLevelThreshold(3) // 2000 XP
 */
export function calculateLevelThreshold(level: number): number {
  return (level - 1) * XP_PER_LEVEL;
}

/**
 * Calculate XP progress within the current level toward the next level.
 * @param totalXp - Total XP earned
 * @param currentLevel - Current level
 * @returns Object with current level XP, XP needed for next level, remaining XP, and progress percentage
 */
export function calculateLevelProgress(
  totalXp: number,
  currentLevel: number
): {
  currentLevel: number;
  currentLevelXp: number;
  xpForNextLevel: number;
  xpRemaining: number;
  progress: number; // 0-100
} {
  // At max level, show full progress
  if (currentLevel >= MAX_LEVEL) {
    return {
      currentLevel,
      currentLevelXp: 0,
      xpForNextLevel: 0,
      xpRemaining: 0,
      progress: 100,
    };
  }

  // Calculate thresholds: if at level 2, show progress from level 2 threshold to level 3 threshold
  const nextLevel = currentLevel + 1;
  const thresholdForNext = calculateLevelThreshold(nextLevel);
  const previousThreshold = calculateLevelThreshold(currentLevel);

  const currentLevelXp = totalXp - previousThreshold;
  const xpNeeded = thresholdForNext - previousThreshold;
  const xpRemaining = Math.max(0, xpNeeded - currentLevelXp);
  const progress = xpNeeded > 0 ? (currentLevelXp / xpNeeded) * 100 : 100;

  return {
    currentLevel,
    currentLevelXp,
    xpForNextLevel: xpNeeded,
    xpRemaining,
    progress: Math.min(100, Math.max(0, progress)),
  };
}

/**
 * Check if a pet is ready to level up to the next level.
 * @param totalXp - Total XP earned
 * @param currentLevel - Current level
 * @returns true if ready to level up
 */
export function canLevelUp(
  totalXp: number,
  currentLevel: number
): boolean {
  if (currentLevel >= MAX_LEVEL) return false;
  const nextLevel = currentLevel + 1;
  const threshold = calculateLevelThreshold(nextLevel);
  return totalXp >= threshold;
}

/**
 * Calculate minimum XP for a given level (the XP threshold for that level).
 * Used during database recovery to estimate XP from blockchain level data.
 *
 * Example: If a pet is level 3, they must have earned at least 2000 XP total.
 * This function returns the minimum XP required to reach that level.
 *
 * @param level - Current level from blockchain
 * @returns Minimum XP needed to have reached that level
 */
export function calculateMinimumXpForLevel(
  level: number
): number {
  // For level N, the minimum XP is the threshold for reaching level N
  return calculateLevelThreshold(level);
}

/**
 * Ensure XP value is at least the minimum required for the given level.
 * Used to correct UI display when backend XP data is inconsistent with level.
 *
 * Problem: During sync, a pet's database XP may be less than what their
 * blockchain level implies (e.g., level 3 but only 50 XP). This function
 * ensures the XP displayed/used is at least the minimum for their level.
 *
 * @param xp - Current XP value (may be incorrect/low)
 * @param level - Current level from blockchain
 * @returns XP value corrected to at least minimum for the level
 */
export function ensureMinimumXpForLevel(
  xp: number,
  level: number
): number {
  const minimumXp = calculateMinimumXpForLevel(level);
  return Math.max(xp, minimumXp);
}

// ============================================================================
// Pet Age Calculations
// ============================================================================

/**
 * Calculate the actual age of a pet in milliseconds, excluding hibernation time.
 * @param birthMs - Timestamp when pet was created (milliseconds since epoch)
 * @param totalHibernatedMs - Cumulative time spent hibernated (milliseconds)
 * @param hibernationStartMs - Timestamp when current hibernation started (0 if not hibernated)
 * @param currentMs - Current timestamp (defaults to Date.now())
 * @returns Age in milliseconds (excluding hibernation time)
 */
export function calculatePetAgeMs(
  birthMs: number,
  totalHibernatedMs: number,
  hibernationStartMs: number,
  currentMs: number = Date.now()
): number {
  // Total time since birth
  const totalLifetimeMs = currentMs - birthMs;

  // Calculate current hibernation duration if currently hibernated
  const currentHibernationMs = hibernationStartMs > 0 ? currentMs - hibernationStartMs : 0;

  // Active age = total lifetime - past hibernation - current hibernation
  const activeAgeMs = totalLifetimeMs - totalHibernatedMs - currentHibernationMs;

  return Math.max(0, activeAgeMs);
}

/**
 * Format pet age into a short human-readable string with two-part precision.
 * Examples: "2y 3mo", "3mo 5d", "5d 2h", "2h 15m"
 * @param ageMs - Age in milliseconds
 * @returns Formatted age string
 */
export function formatPetAge(ageMs: number): string {
  const seconds = Math.floor(ageMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  // Calculate remainder values for second unit
  const remainingMonths = months % 12;
  const remainingDays = days % 30;
  const remainingHours = hours % 24;
  const remainingMinutes = minutes % 60;

  if (years >= 1) {
    const monthsPart = remainingMonths > 0 ? ` ${remainingMonths}mo` : '';
    return `${years}y${monthsPart}`;
  }
  if (months >= 1) {
    const daysPart = remainingDays > 0 ? ` ${remainingDays}d` : '';
    return `${months}mo${daysPart}`;
  }
  if (days >= 1) {
    const hoursPart = remainingHours > 0 ? ` ${remainingHours}h` : '';
    return `${days}d${hoursPart}`;
  }
  if (hours >= 1) {
    const minutesPart = remainingMinutes > 0 ? ` ${remainingMinutes}m` : '';
    return `${hours}h${minutesPart}`;
  }
  if (minutes >= 1) {
    return `${minutes}m`;
  }
  return 'just born';
}

// ============================================================================
// Species Helper Functions
// ============================================================================

import type { ElementId, SuikamonSpecies } from './petModels.js';
import { Rarity, ELEMENT_EMOJI, RARITY_COLORS, rarityToNumber } from './petModels.js';
import { GAME_CONSTANTS, MAX_LEVEL, REFERRAL_CONSTANTS } from './gameConstants.js';
import { SUIKAMON_SPECIES } from './speciesConfig.js';

/**
 * Get emoji for a given element.
 */
export function getElementEmoji(element: ElementId): string {
  return ELEMENT_EMOJI[element];
}

/**
 * Get CSS gradient class for a given element.
 */
export function getElementGradientClass(element: ElementId): string {
  return `suikamon-gradient-${element.toLowerCase()}`;
}

/**
 * Get color hex code for a given rarity.
 */
export function getRarityColor(rarity: Rarity | undefined): string {
  if (!rarity) return RARITY_COLORS[Rarity.Common];
  return RARITY_COLORS[rarity];
}

/**
 * Ordered list of rarity names by numeric rarity level.
 */
export const RARITY_NAMES_BY_LEVEL: readonly Rarity[] = [
  Rarity.Common,
  Rarity.Rare,
  Rarity.Epic,
  Rarity.Legendary,
  Rarity.Unique,
];

/**
 * Convert numeric rarity level back to its name.
 */
export function getRarityNameByLevel(rarityLevel: number): Rarity {
  return RARITY_NAMES_BY_LEVEL[rarityLevel] ?? RARITY_NAMES_BY_LEVEL[0];
}

/**
 * Get supply cap for a given rarity.
 * Returns max number of pets that can be minted for this rarity tier.
 */
export function getRaritySupplyCap(rarity: Rarity | undefined): number | null {
  if (!rarity) return GAME_CONSTANTS.RARITY_CAP_COMMON;
  switch (rarity) {
    case Rarity.Common: return GAME_CONSTANTS.RARITY_CAP_COMMON;
    case Rarity.Rare: return GAME_CONSTANTS.RARITY_CAP_RARE;
    case Rarity.Epic: return GAME_CONSTANTS.RARITY_CAP_EPIC;
    case Rarity.Legendary: return GAME_CONSTANTS.RARITY_CAP_LEGENDARY;
    case Rarity.Unique: return GAME_CONSTANTS.RARITY_CAP_UNIQUE;
    default: return null;
  }
}

/**
 * Get supply cap for a specific species (based on its rarity).
 */
export function getSpeciesSupplyCap(species: SuikamonSpecies): number | null {
  return getRaritySupplyCap(species.rarity);
}

/**
 * Get supply cap for a given numeric rarity level.
 * Converts blockchain numeric rarity (0-4) to supply cap.
 * Used by backend mint validation and availability checks.
 *
 * @param rarityLevel - Numeric rarity level (0=Common, 1=Rare, 2=Epic, 3=Legendary, 4=Unique)
 * @returns Supply cap for this rarity tier
 */
export function getRarityCapByLevel(rarityLevel: number): number {
  switch (rarityLevel) {
    case 0: return GAME_CONSTANTS.RARITY_CAP_COMMON;
    case 1: return GAME_CONSTANTS.RARITY_CAP_RARE;
    case 2: return GAME_CONSTANTS.RARITY_CAP_EPIC;
    case 3: return GAME_CONSTANTS.RARITY_CAP_LEGENDARY;
    case 4: return GAME_CONSTANTS.RARITY_CAP_UNIQUE;
    default: return GAME_CONSTANTS.RARITY_CAP_COMMON;
  }
}

/**
 * Create a map of all rarity levels to their supply caps.
 * Used by backend availability checks to validate mint counts.
 *
 * @returns Record mapping rarity level (0-4) to supply cap
 */
export function getRarityCapMap(): Record<number, number> {
  return {
    0: getRarityCapByLevel(0),
    1: getRarityCapByLevel(1),
    2: getRarityCapByLevel(2),
    3: getRarityCapByLevel(3),
    4: getRarityCapByLevel(4),
  };
}

/**
 * Generate a URL-safe slug from species name.
 * Used for constructing image URLs and other identifiers.
 */
export function getSpeciesSlug(speciesId: number, species: SuikamonSpecies[]): string {
  const found = species.find(s => s.id === speciesId);
  if (!found) {
    return `species${speciesId}`;
  }

  const normalized = found.name.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
  const slug = normalized.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return slug || `species${speciesId}`;
}

/**
 * Derive the evolution stage a pet of this species would display at a given level.
 * stageLevels[N] = level required to evolve FROM stage N TO stage N+1.
 * Clamped to [1, maxStage] \u2014 callers use this for hatched (non-egg) pets only.
 */
export function getStageForLevel(speciesId: number, level: number): number {
  const species = getSpeciesById(speciesId);
  if (!species) return 1;
  const stageLevels = species.behavior.stageLevels;
  let stage = 0;
  for (let i = 0; i < stageLevels.length; i++) {
    if (level >= stageLevels[i]) stage = i + 1;
  }
  return Math.max(1, Math.min(stage, species.behavior.maxStage));
}

/**
 * NFT/species image cache version - increment whenever pet artwork is replaced
 * at the same filenames (evolution images, species previews, backgrounds).
 * Shared by the web app (suikamon-web/src/utils/petHelpers.ts) and the backend
 * (minting, evolution, Telegram bot) so a single bump busts caches everywhere.
 * Format: Major.Minor (e.g., "1.0", "1.1", "2.0")
 */
export const NFT_IMAGE_VERSION = '1.9';

/**
 * Generate NFT image URL for a pet at a specific evolution stage.
 * Handles both egg stage (generic image) and evolved stages (species-specific).
 * Used by backend for minting and evolution.
 *
 * @param speciesId - Species ID (0-N)
 * @param stage - Evolution stage (0=egg, 1=baby, 2=adult, etc.)
 * @param publicUrl - Optional base URL (defaults to https://suikamon.com)
 * @param species - Species array to look up species data
 * @returns Full image URL
 *   - Stage 0 (egg): https://suikamon.com/species/egg.png?v=1.6
 *   - Other stages: https://suikamon.com/species/bireon-evolution1.png?v=1.6
 */
export function getPetImageUrl(
  speciesId: number,
  stage: number,
  publicUrl: string = 'https://suikamon.com',
  species: SuikamonSpecies[]
): string {
  // Stage 0 (egg) - use generic egg image (all species share the same egg)
  if (stage === 0) {
    return `${publicUrl}/species/egg.png?v=${NFT_IMAGE_VERSION}`;
  }

  // All other stages - use species-specific evolution images
  const slug = getSpeciesSlug(speciesId, species);
  return `${publicUrl}/species/${slug}-evolution${stage}.png?v=${NFT_IMAGE_VERSION}`;
}


/**
 * Re-export rarityToNumber utility for blockchain numeric representation.
 * Converts Rarity enum to number (0=Common, 1=Rare, 2=Epic, 3=Legendary, 4=Unique).
 */
export { rarityToNumber };

// ============================================================================
// Species Lookup and Query Functions
// ============================================================================

/**
 * Looks up a species by its ID.
 * @param speciesId - The species ID to find
 * @returns The species definition, or undefined if not found
 */
export function getSpeciesById(speciesId: number): SuikamonSpecies | undefined {
  return SUIKAMON_SPECIES.find(s => s.id === speciesId);
}

/**
 * Returns the display name for a species, falling back to "Species #<id>" for unknown IDs.
 * Prefer this over inline SUIKAMON_SPECIES.find() calls.
 */
export function getSpeciesName(speciesId: number): string {
  return getSpeciesById(speciesId)?.name ?? `Species #${speciesId}`;
}

/**
 * Looks up a species by its name (case-insensitive).
 * @param name - The species name to find
 * @returns The species definition, or undefined if not found
 */
export function getSpeciesByName(name: string): SuikamonSpecies | undefined {
  return SUIKAMON_SPECIES.find(s => s.name.toLowerCase() === name.toLowerCase());
}

/**
 * Gets all available (non-hidden) species.
 * @returns Array of available species definitions
 */
export function getAvailableSpecies(): SuikamonSpecies[] {
  return SUIKAMON_SPECIES.filter(s => s.available);
}

/**
 * Checks if a species ID is valid and exists.
 * @param speciesId - The species ID to check
 * @returns true if species exists
 */
export function isValidSpeciesId(speciesId: number): boolean {
  return SUIKAMON_SPECIES.some(s => s.id === speciesId);
}

/**
 * Gets the maximum evolution stage for a species.
 * @param speciesId - The species ID
 * @returns Maximum stage number, or 0 if species not found
 */
export function getMaxStage(speciesId: number): number {
  const species = getSpeciesById(speciesId);
  return species?.behavior.maxStage ?? 0;
}

/**
 * Gets the display name for a pet's evolution stage.
 * @param stage - Current stage (0 = egg, 1+ = evolved forms)
 * @param speciesId - The species ID to determine max stage
 * @returns Human-readable stage name: "Egg", "Max Evolution", or "Evolution X"
 */
export function getStageName(stage: number, speciesId: number): string {
  if (stage === 0) return "Egg";
  const maxStage = getMaxStage(speciesId);
  if (stage >= maxStage) return "Max Evolution";
  return `Evolution ${stage}`;
}

/**
 * Gets the level requirement for a specific stage.
 * @param speciesId - The species ID
 * @param stage - The stage to get level requirement for
 * @returns Level required for this stage, or null if invalid
 */
export function getStageLevelRequirement(speciesId: number, stage: number): number | null {
  const species = getSpeciesById(speciesId);
  if (!species) return null;
  return species.behavior.stageLevels[stage] ?? null;
}

/**
 * Gets XP range for a species action.
 * @param speciesId - The species ID
 * @returns Object with baseXpMin and baseXpMax, or null if species not found
 */
export function getSpeciesXpRange(speciesId: number): { baseXpMin: number; baseXpMax: number } | null {
  const species = getSpeciesById(speciesId);
  if (!species) return null;
  return {
    baseXpMin: species.behavior.baseXpMin,
    baseXpMax: species.behavior.baseXpMax,
  };
}

/**
 * Get the maximum level for a species (level required for final evolution).
 * @param speciesId - The species ID
 * @returns Maximum level for this species, or null if not found
 */
export function getMaxLevelForSpecies(speciesId: number): number | null {
  const species = getSpeciesById(speciesId);
  if (!species) return null;

  const stageLevels = species.behavior.stageLevels;
  if (!stageLevels || stageLevels.length === 0) return null;

  return stageLevels[stageLevels.length - 1];
}

// ============================================================================
// Adoption Cap Calculations
// ============================================================================

/**
 * Calculate adoption metrics for a species based on breeding reserve percentage.
 *
 * This function determines how many eggs can be adopted directly vs how many
 * are reserved for breeding. Both frontend and backend use this shared logic
 * to ensure consistent cap enforcement.
 *
 * Example (Bireon: 500 total cap, 30% breeding reserve):
 * - adoptionCap = 350 (70% of 500)
 * - breedingReserved = 150 (30% of 500)
 * - When totalMinted = 300: adoptionRemaining = 50
 * - When totalMinted = 351: adoptionRemaining = 0 (adoption disabled)
 *
 * @param species - Species configuration
 * @param supplyCap - Total supply cap for this species' rarity (from game constants)
 * @param totalMinted - Total number of pets minted (all sources)
 * @returns Object with adoptionCap, adoptionRemaining, and breedingReserved
 */
export function calculateAdoptionMetrics(
  species: SuikamonSpecies,
  supplyCap: number | null,
  totalMinted: number
): {
  adoptionCap: number | null;
  adoptionRemaining: number | null;
  breedingReserved: number | null;
} {
  // Get breeding reserve percentage (defaults to 0 if not set)
  const breedingReservedPercentage = species.breedingReservedPercentage ?? 0;

  // Calculate how many eggs are reserved for breeding
  const breedingReserved = supplyCap !== null
    ? Math.floor(supplyCap * breedingReservedPercentage / 100)
    : null;

  // Calculate adoption cap = total supply - breeding reserve
  const adoptionCap = supplyCap !== null && breedingReserved !== null
    ? supplyCap - breedingReserved
    : supplyCap;

  // Calculate how many more can be adopted before hitting adoption cap
  const adoptionRemaining = adoptionCap !== null
    ? Math.max(adoptionCap - totalMinted, 0)
    : null;

  return {
    adoptionCap,
    adoptionRemaining,
    breedingReserved,
  };
}

/**
 * Calculate the discounted adoption price for a species.
 * The discount is a multiplier where 1 = 100% (no discount), <1 = on sale (e.g., 0.5 = 50% off).
 *
 * @param basePrice - Base adoption price in SUI tokens
 * @param discount - Price multiplier (1 = no discount, default 1)
 * @returns Discounted price rounded to 2 decimal places
 * @example
 * calculateDiscountedPrice(10, 1) // 10 (no discount)
 * calculateDiscountedPrice(10, 0.5) // 5 (50% discount)
 * calculateDiscountedPrice(9.99, 0.75) // 7.49 (25% discount)
 */
export function calculateDiscountedPrice(basePrice: number, discount: number = 1): number {
  const discountedPrice = basePrice * discount;
  // Round to 2 decimal places for currency
  return Math.round(discountedPrice * 100) / 100;
}

/**
 * Calculate the discount percentage from a discount multiplier.
 * The discount is a multiplier where 1 = 0% off, 0.5 = 50% off, 0.75 = 25% off.
 *
 * @param discount - Price multiplier (1 = no discount)
 * @returns Discount percentage as whole number (0-100)
 * @example
 * calculateDiscountPercentage(1) // 0 (no discount)
 * calculateDiscountPercentage(0.5) // 50 (50% off)
 * calculateDiscountPercentage(0.75) // 25 (25% off)
 */
export function calculateDiscountPercentage(discount: number = 1): number {
  return Math.round((1 - discount) * 100);
}

/**
 * Calculate the total discount percentage using ADDITIVE discount logic.
 * Species discount % and referral discount % are added together.
 *
 * Formula (ADDITIVE):
 * - Species discount % = (1 - speciesDiscount) × 100
 * - Referral discount % = 10 (if has referral)
 * - Total discount % = species % + referral %
 *
 * @param basePrice - Original adoption price in SUI tokens (not used in additive calc)
 * @param speciesDiscount - Species discount multiplier (1 = no discount, 0.5 = 50% off, 0.3 = 70% off)
 * @param hasReferral - Whether a referral discount is applied (always 10% off)
 * @returns Total discount percentage (0-100)
 * @example
 * calculateTotalDiscountPercentage(10, 1, false) // 0 (no discount)
 * calculateTotalDiscountPercentage(10, 0.5, false) // 50 (50% species discount only)
 * calculateTotalDiscountPercentage(10, 0.5, true) // 60 (50% species + 10% referral)
 * calculateTotalDiscountPercentage(10, 0.3, true) // 80 (70% species + 10% referral)
 */
export function calculateTotalDiscountPercentage(
  _basePrice: number,
  speciesDiscount: number,
  hasReferral: boolean
): number {
  const speciesDiscountPercent = calculateDiscountPercentage(speciesDiscount);
  const referralDiscountPercent = hasReferral ? REFERRAL_CONSTANTS.USER_DISCOUNT_PERCENT : 0;
  const totalDiscountPercent = speciesDiscountPercent + referralDiscountPercent;
  return Math.round(totalDiscountPercent);
}
